import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, FormControl, Modal, SplitButton } from "react-bootstrap";
import {autorun, runInAction, toJS} from "mobx";
import {inject, observer} from "mobx-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from 'uuid';
import LoadingAnimation from "./LoadingAnimation";
import Textarea from "react-expanding-textarea";
import { DEV, getConfig, getVersionInfo } from "../lib/configMgr";
import CopyToClipboardButton from "./CopyToClipboardButton";
import {
  captureMessage, formatTimestamp, isImage,
  parseContentDisposition, safeParseJSON,
  saveBlobContent, setPageTitle, TS,
  uploadFile,
} from "../lib/utils";
import { extractSearchParams } from "../tools";
import { ChatSettings } from "./ChatSettings";
import { extractVersion } from "./modelVersion";
import SearchContext from "../lib/SearchContext";
import SimpleSelect from "./controls/SimpleSelect";
import FileAttachments from "./controls/FileAttachments";
import Checkbox from "./controls/Checkbox";
import MemoryWindow from "./MemoryWindow";
import { shouldShowMemoryWindow, shouldShowVizCanvas } from "../llm/agentGating";
import VisualizationCanvas from "./canvas/VisualizationCanvas";
import { visualizationStore } from "../stores/VisualizationStore";
import { useAutoScroll } from "../hooks/useAutoScroll";
import { interruptionSite } from "../llm/interruption";
import { useDebounce } from "../hooks/useDebounce";
import {
  AGENT_COACH,
  AGENT_GUIDE,
  AGENT_HELP, cleanTurn,
  DOC_LIMIT,
  DOC_SIZE_LIMIT,
  FILE_INPUT_ACCEPT,
  IMAGE_LIMIT,
  IMAGE_SIZE_LIMIT,
  ROLE_ASSISTANT,
  ROLE_SYSTEM,
  ROLE_USER,
  TOOL_SEARCH_ANALYST,
  TOOL_RUN_AGENT,
  optimizeAgentMessages
} from "../llm/LLMClient";
import { oneshot } from "../queries/autosuggest";
import { Conversation } from "../llm/Conversation";
import { loadStoredConversation, markIfTurnRunning, useRunningTurnWatch } from "../llm/liveTurn";
import { withoutDisplayOnly } from "../llm/resume";
import { SafeRenderer } from "./SafeRenderer";
import { calculateCost } from "../llm/modelPricing";
import { ConversationProvider } from "../contexts/ConversationContext";
import SourceReferencesDrawer from "./SourceReferencesDrawer";
import { rootStore } from "../stores";
import { useInitialConversation, useWelcomeOnboarding } from "./InitialConversation";
import Analytics from "../lib/Analytics";
import MolecularEditorModal from "./MolecularEditorModal";
import SharedCopyNotice from "./SharedCopyNotice";
import { discardPlan, forkLineage, pendingFork, takePendingFork } from "../llm/forkLineage";
import molEditorImage from "../img/mol-editor-glyphicon.png";
import useSourceMetadata from "../hooks/useSourceMetadata";

const { ExternalRESTAPI, useConverseStream, withAutoplex } = getConfig();
const WELCOME = "Welcome to the Plex LLM!";

const appendModelId = (model) => {
  //return DEV ? ` (${model})` : "";
  return "";
};

const extractConversationText = (role, content, includeToolUse = false, dsConfig = 0) => {
  return content.map(contentBlock => {
    const {text = null, toolUse = null, toolResult = null} = contentBlock;
    const toolName = toolUse?.name;
    // Ignore these, they're not interesting
    if (role === ROLE_ASSISTANT) {
      if (toolName === TOOL_SEARCH_ANALYST) {
        // The same link as the call's "search again" button (SubmitSearchButton): its params come
        // from the call's input, not the call itself, or the link has no ids (PLEX-6538). A call
        // still streaming has no ids yet, so it gets no link.
        const params = extractSearchParams(toolUse.input, dsConfig);
        const url = SearchContext.getAppSearchURL({searchIDs: params.ids, dsConfig, ...params}, {}, {withLabels: true});
        return `Plexy: ${toolUse.input?.instructions}${TS}${url ? `${url}${TS}` : ""}`;
      }
      return toolName && !includeToolUse ? "" : `Plexy: ${text || toolName}${TS}`;
    }
    if (toolResult) {
      if (toolResult.content && toolResult.content[0]?.json?.options) {
        return `Resolved IDs: ${toolResult.content[0].json.options.filter(el => el).map(el => el.id).join(", ")}${TS}`;
      }
      return includeToolUse ? extractConversationText("Result", toolResult.content, includeToolUse, dsConfig) : "";
    }
    return `${role === ROLE_USER ? "User" : role === ROLE_SYSTEM ? "System" : role}: ${text || JSON.stringify(contentBlock)}${TS}`;
  }).join("");
};

export const ChatSession = inject('sourceReferencesStore')(observer(({className = null, client, userStore, appStatusStore, sourceReferencesStore, onSubmit = null,
                              showWelcome = false,
                              autoFocus = false,
                              searchParams = null, searchContextKey = null, historyId = null, historyType = 'conversation', categories = null, showSettings = true,
                              useTools = false, useMarkdown = true,
                              waitingForResults = false,
                              allowAttachments = false, sessionAgent = AGENT_GUIDE,
                              resolveSourceLinks = null,
                              showExports = true,
                              onReturnToSearch = null,
                              loadInitialConversation = false,
                              showConversationHistory = true,
                              onDownloadContextChange = null,
                              modalContentRef = null,
                              showOverlay = false,
                              onHistoryLoadStart = null,
                              onHistoryLoadEnd = null,
                              initialTemplates = {}}) => {
  const {
    turns: convo,
    agentId,
    streamId,
    model,
    sendPrompt,
    waitingForResponse,
    interrupt,
    replayFrom,
    resumeFrom,
    replayKeptTurns,
    discardAfter,
    loadConversation,
    inputTokens,
    outputTokens,
    totalTokens,
    cacheReadInputTokens,
    cacheWriteInputTokens,
    latencyMs,
  } = client;

  const helpOnly = sessionAgent === AGENT_HELP;
  // Sentinel UUID for the welcome conversation — never saved to history
  const WELCOME_CONVERSATION_ID = '00000000-0000-0000-0000-000000000001';

  const {aiModels = {}, aiModelDefault = null, aiTemplates = {}, dsConfig = 0} = appStatusStore;
  const {isAdmin, isPowerUser, showAdmin, authData: auth, userName} = userStore;

  const showMemoryWindow = shouldShowMemoryWindow({
    memoryEnabled: appStatusStore.versionInfo?.memory_enabled,
    isAdmin,
    sessionAgent,
  });

  const showVizCanvas = shouldShowVizCanvas({
    vizEnabled: appStatusStore?.vizEnabled ?? true,
    sessionAgent,
    canvasShouldShow: visualizationStore.canvasShouldShow,
  });

  // Always start with drawer closed; don't restore from localStorage on mount
  useEffect(() => {
    sourceReferencesStore.closeDrawer();
  }, [sourceReferencesStore]);

  // PLEX-5920: the evidence tool results' source_metadata (the analyst's, in sub-agent turns)
  // describes the URLs the answers cite, so their source cards need not fetch the pages' own
  useSourceMetadata(client, sourceReferencesStore);

  // Clear sources when starting new conversation
  // This triggers when convo becomes empty (e.g., "New Conversation" action)
  useEffect(() => {
    if (convo && convo.length === 0) {
      sourceReferencesStore.clearSources();
    }
  }, [convo, sourceReferencesStore]);

  // Track whether initial conversation was loaded in this session
  const [initialConversationLoaded, setInitialConversationLoaded] = useState(false);

  // Show informational toast when autoplex is disabled
  useEffect(() => {
    if (!withAutoplex) {
      toast.info("LLM capabilities are limited to conversation. Automatic search and tool use are unavailable.", {
        autoClose: 8000,
        toastId: 'autoplex-disabled-notice' // Prevent duplicate toasts
      });
    }
  }, []); // Empty deps - only run once on mount since withAutoplex won't change

  const [isDownloadingPPTX, setIsDownloadingPPTX] = useState(false);
  const [isDownloadingDOCX, setIsDownloadingDOCX] = useState(false);
  // Inline dialog state (replaces native browser prompt/confirm)
  const [bugReportDialog, setBugReportDialog] = useState({ open: false, message: "", json: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });
  // Title is stored directly in conversationContext.title, no separate state needed
  // Track conversation context (id, authors, title)
  // pendingLoadClearRef: set true when a conversation load starts so the
  // useLayoutEffect below knows to fire onHistoryLoadEnd after turns are painted.
  const pendingLoadClearRef = useRef(false);
  // Incremented on every load so useLayoutEffect fires even when convo.length is unchanged
  const [loadCount, setLoadCount] = useState(0);

  // Track conversation ID separately (matches top-level id from server)
  const [conversationId, setConversationId] = useState(null);
  // Conversation context includes:
  // - authors: list of users who have edited this conversation
  // - title: human-readable title
  const [conversationContext, setConversationContext] = useState(null);
  // True once the LLM-generated title has been set, so we only generate once per conversation
  const titleGeneratedRef = useRef(false);
  // Track drag-over state for visual feedback
  const [isDragOver, setIsDragOver] = useState(false);
  // Escalating wait status: null until we've been waiting >15s with no stream events.
  const [waitStatus, setWaitStatus] = useState(null);
  const waitStatusTimerRef = useRef(null);
  const isStreaming = streamId != null;
  const busy = waitingForResponse || isStreaming;


  const showAdvancedFeatures = isAdmin ? showAdmin : isPowerUser;
  const isIFrame = window.self !== window.top;
  const inputRef = useRef();
  const composerFileAttachmentsRef = useRef();
  const conversationIdRef = useRef(null); // Track conversation ID to prevent generating new UUIDs on every save
  const userDiscardedRef = useRef(false); // Set when user explicitly clears conversation; blocks history reload
  // PLEX-6383: the fork a mid-conversation discard left for the next prompt to name
  // (fork_of/fork_base), so the backend writer can copy the kept turns from the source.
  const pendingForkRef = useRef(null);
  // PLEX-6757: the id of a restored conversation whose lookup found nothing. Another user's
  // conversation now reads as missing too (Tim's 404 rule), so its first save asks for a copy
  // (copy=true): the backend keeps the id when it is free and mints one when it isn't (rd #280).
  const restoreCopyIdRef = useRef(null);
  // True while a conversation-type historyId's turns haven't been fetched/restored yet (e.g.
  // waiting on userStore.isAuthenticated after a page reload). Blocks sendChatMessage from
  // treating the still-empty convo as brand new — see PLEX-6391/WEBAPP-E1: a message sent
  // during this window silently minted a fresh conversation id, orphaning the original history.
  const historyRestorePendingRef = useRef(!!historyId && historyType !== 'search');
  // Holds the latest sendChatMessage (redefined every render). Deferred callers (setTimeout)
  // must call sendChatMessageRef.current(...) instead of closing over sendChatMessage directly —
  // a direct closure captured before a discardConversation()-triggered re-render still sees the
  // pre-discard conversationId/convo state, misclassifying a fresh restart as a fork and routing
  // the history save through UPDATE instead of CREATE (404 on PUT to a never-created record).
  const sendChatMessageRef = useRef(null);

  const useRemoteTools = true;

  const [promptText, setPromptText] = useState("");
  const [chatSettings, setChatSettings] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editedTemplates = useMemo(() => ({...initialTemplates}), []);
  const [selectedTemplate, setSelectedTemplate] = useState(showAdvancedFeatures ? "extra-prompt" : null);
  const [templateText, setTemplateText] = useState("");
  const [showTemplateEditor, setShowTemplateEditor] = useState(showAdvancedFeatures);
  const [applyCustomizations, setApplyCustomizations] = useState(showAdvancedFeatures);
  const [templateVersion, setTemplateVersion] = useState(0);
  // Transient attachment information
  const [attachments, setAttachments] = useState([]);
  // Molecular editor state
  const [showMolEditor, setShowMolEditor] = useState(false);

  // Admin-only flags to disable prompts and tools
  const [disablePrompts, setDisablePrompts] = useState(false);
  const [disableTools, setDisableTools] = useState(false);

  // Admin-only streaming replay test
  const [isStreamReplaying, setIsStreamReplaying] = useState(false);
  const streamReplayAbortRef = useRef(null);

  // Prompt editing state
  const [editingTurnIndex, setEditingTurnIndex] = useState(null);

  // Check if there are any partial messages
  const hasPartialMessages = convo.some(turn =>
    turn && (turn.partial || (turn.content && turn.content.some(block => block && block.partial)))
  );
  // Guide gets muted styling only once a flavor agent has actually done work in this
  // conversation — in non-flavor mode the guide's own text is the primary content.
  const hasFlavorAgent = convo.some(turn =>
    turn && turn.content && turn.content.some(block => block?.toolUse?.name === TOOL_RUN_AGENT)
  );
  // Check if any attachments are still uploading (missing uploadId and not errored out)
  const hasActiveUploads = attachments?.some(file => !file.uploadId && !file.error) || false;

  /**
   * Generates a simple fallback title from the first user prompt.
   */
  const generateFallbackTitle = useCallback((turns) => {
    if (!turns || turns.length === 0) {
      return "Untitled Conversation";
    }

    const firstUserTurn = turns.find(turn =>
      turn != null && turn.role === ROLE_USER &&
      turn.content?.some(block => block.text)
    );

    if (!firstUserTurn) {
      return "Untitled Conversation";
    }

    const text = firstUserTurn.content
      .filter(block => block.text)
      .map(block => block.text)
      .join(" ")
      .trim();

    return text ? text.substring(0, 60) : "Untitled Conversation";
  }, []);

  /**
   * Creates a conversation object with full context for saving/exporting.
   * Format: { context: {...}, turns: [...] }
   */
  const conversationWithContext = useCallback((turns, searchParams, editedTemplates, addCurrentUser = false) => {
    const { cell } = getConfig();
    const currentUser = userStore.userName;

    // Initialize authors from context
    let authors = conversationContext?.authors ? [...conversationContext.authors] : [];

    // If addCurrentUser is true, ensure current user is in the list
    if (addCurrentUser && currentUser && !authors.includes(currentUser)) {
      authors.push(currentUser);
      setConversationContext(prev => ({
        ...prev,
        authors: [...authors]
      }));
    }

    // Build context - only include authors if we have them
    const context = {
      title: conversationContext?.title || generateFallbackTitle(turns),
      searchParams,
      editedTemplates,
      cell,
      version: getVersionInfo().version,  // Extract inner version object
    };

    // Add optional fields
    if (authors.length > 0) {
      context.authors = authors;
    }
    if (model) {
      context.model = model;
    }

    // If searchParams present AND this is a search-based conversation,
    // set context.url to mark it for the SEARCH table (vs CONVERSATION table)
    if (searchParams && historyType === 'search') {
      const searchURL = SearchContext.getAppSearchURL(searchParams);
      if (searchURL) context.url = searchURL.toString();
    }

    // Nested run_agent/run_search_analyst sub-agent turns live in the flat,
    // top-level client.subAgentTurns map (PLEX-6289's flatten refactor), not
    // per-turn anymore — this save payload must carry them the same way
    // LLMClient's own conversationWithContext() does, or every nested search
    // (and its "re-run search" button) silently vanishes on save, regardless
    // of how long the request took (PLEX-6300 follow-up, WEBAPP-CJ redux).
    const subAgentTurns = {};
    client.subAgentTurns.forEach((messages, toolUseId) => {
      const optimized = optimizeAgentMessages(toJS(messages));
      if (optimized) subAgentTurns[toolUseId] = optimized;
    });

    return {
      context,
      turns: withoutDisplayOnly(turns).map(turn => cleanTurn(toJS(turn), true)),
      subAgentTurns,
    };
  }, [conversationContext, userStore, model, generateFallbackTitle, client]);

  /**
   * Convert conversation turns to text representation
   */
  const conversationAsText = useCallback((turns, includeToolUse = false) => {
    // Convert all visible entries into a text representation
    return turns
      .reduce((result, turn) => turn ? `${result}\n${extractConversationText(turn.role, turn.content, includeToolUse, dsConfig)}` : result, "");
  }, [dsConfig]);

  /**
   * Generates a human-readable title from the conversation using LLM.
   * This is called asynchronously when saving a new conversation.
   */
  const generateConversationTitleViaLLM = useCallback(async (turns) => {
    if (!turns || turns.length === 0) {
      return "plex-conversation";
    }

    try {
      // Get conversation text for title generation (limit to first few turns to keep it fast)
      const limitedTurns = turns.slice(0, 3);
      const conversationText = conversationAsText(limitedTurns, false);
      if (!conversationText.trim()) {
        return generateFallbackTitle(turns);
      }

      // Use oneshot to generate a concise title
      const prompt = `Generate a concise, descriptive title for this conversation.
Use natural language with proper capitalization. Maximum 60 characters.
Only return the title, nothing else.

Conversation:
${conversationText}`;

      const options = {
        input_format: "text",
        output_format: "text",
      };

      const response = await oneshot(prompt, auth, options);
      const titleText = await response.text();

      // Clean up the title - remove quotes and extra whitespace
      const title = titleText
        .trim()
        .replace(/^["']|["']$/g, '') // Remove surrounding quotes
        .substring(0, 60);

      return title || generateFallbackTitle(turns);
    } catch (e) {
      console.warn("Failed to generate conversation title via LLM, using fallback", e);
      return generateFallbackTitle(turns);
    }
  }, [conversationAsText, generateFallbackTitle, auth]);

  // Strip any existing fork suffix and append a new one.
  const applyTitleSuffix = useCallback((title, suffix) => {
    const base = (title || '').replace(/\s*\((truncated at turn|regenerated from turn)\s+\d+\)\s*$/, '').trimEnd();
    return base ? `${base} ${suffix}` : suffix;
  }, []);

  /**
   * Hash conversation turns for comparison
   */
  const hashConversation = useCallback(async (turns) => {
    const serialized = JSON.stringify(turns.filter(turn => turn != null).map(turn => ({
      role: turn.role,
      content: turn.content
    })));
    const encoder = new TextEncoder();
    const data = encoder.encode(serialized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  /**
   * Saves the current conversation to cloud for history drawer.
   * - UUID already set by caller (sendChatMessage or restoreConversation)
   * - Updates lastModified timestamp on every save for sorting
   * - Saves to cloud backend (DynamoDB/S3)
   */
  const saveConversationToHistory = useCallback(async (explicitIsNew = null, promptText = null) => {
    console.debug('[saveConversationToHistory] Called', {
      helpOnly,
      convoLength: convo.length,
      conversationId: conversationIdRef.current,
      explicitIsNew,
      promptText
    });

    if (helpOnly || conversationIdRef.current === WELCOME_CONVERSATION_ID) {
      console.debug('[saveConversationToHistory] Skipping save - helpOnly or welcome conversation');
      return;
    }

    // For explicitly new conversations, allow saving even if convo is empty (we have the prompt)
    // For existing conversations (explicitIsNew === false or null), require turns
    if (explicitIsNew !== true && convo.length === 0) {
      console.debug('[saveConversationToHistory] Skipping save - no turns yet and not explicitly new');
      return;
    }

    // Use ref for immediate access (state updates are async)
    const currentId = conversationIdRef.current;
    if (!currentId) {
      console.debug('saveConversationToHistory called without UUID - caller should set conversationId first');
      return;
    }

    try {
      // For explicitly new conversations with no turns yet, create minimal conversation data
      let conversationData;
      if (explicitIsNew === true && convo.length === 0 && promptText) {
        // Create minimal conversation with just the prompt as a user turn
        const { cell } = getConfig();
        conversationData = {
          context: {
            searchParams,
            editedTemplates,
            authors: [userName],
            cell,
            model,
          },
          turns: [{
            role: ROLE_USER,
            content: [{ text: promptText }]
          }]
        };
        console.debug('[saveConversationToHistory] Created minimal conversation data for new conversation');
      } else {
        conversationData = conversationWithContext(convo, searchParams, editedTemplates, true);
      }

      // Store UUID at TOP LEVEL (not in context) - this matches server structure
      conversationData.id = currentId;
      // Don't send timestamp fields - backend manages created/modified

      // Check if this is the first save (CREATE vs UPDATE)
      const { historyStore } = rootStore;

      // Prefer the store's title over local state — the user may have renamed the
      // conversation via the history sidebar, which patches the store directly without
      // updating conversationContext here.
      const storedTitle = historyStore?.conversations?.find(c => c.id === currentId)?.context?.title;
      if (storedTitle && conversationData.context) {
        conversationData.context.title = storedTitle;
      }
      let isNewConversation;
      if (explicitIsNew !== null) {
        // Use explicit flag when provided
        isNewConversation = explicitIsNew;
        console.debug('[saveConversationToHistory] Using explicit isNew flag:', isNewConversation);
      } else {
        // Fall back to checking cache
        isNewConversation = false;
        if (historyStore?.isOnline && userStore?.isAuthenticated) {
          // Check if conversation exists in history store
          isNewConversation = !historyStore.conversationCache.has(currentId);
        }
        console.debug('[saveConversationToHistory] Inferred isNew from cache:', isNewConversation);
      }

      const isSearchBasedConversation = conversationData.context?.url || conversationData.context?.searchParams;

      // Generate LLM title on the first UPDATE after the assistant has responded
      // (titleGeneratedRef guards against re-running on subsequent turns)
      const hasAssistantTurn = conversationData.turns?.some(t => t.role === 'assistant');
      const shouldGenerateTitle = !isNewConversation && !titleGeneratedRef.current && hasAssistantTurn;
      if (shouldGenerateTitle) {
        titleGeneratedRef.current = true;
        try {
          const generatedTitle = await generateConversationTitleViaLLM(conversationData.turns);
          conversationData.context.title = generatedTitle;
          setConversationContext(prev => ({ ...prev, title: generatedTitle }));
          console.debug('[saveConversationToHistory] Generated title via LLM:', generatedTitle);
        } catch (e) {
          console.warn("Failed to generate title via LLM, keeping prompt-text title", e);
        }
      }

      // Add metadata for cloud storage
      conversationData.metadata = {
        turnCount: conversationData.turns?.length || 0,
        model: model || conversationData.context.model,
      };

      // Save to cloud via HistoryStore
      if (historyStore?.isOnline && userStore?.isAuthenticated) {
        console.debug('[saveConversationToHistory] Saving to cloud', { isNewConversation, currentId, title: conversationData.context?.title });
        // Explicit: has ID = UPDATE, no ID = CREATE
        if (isNewConversation) {
          // CREATE new conversation in cloud (first prompt submission).
          // Title is set client-side from the prompt text; LLM title generated on first onTurnComplete.
          const _saveStart = Date.now();
          const copy = restoreCopyIdRef.current === currentId;
          if (copy) restoreCopyIdRef.current = null;
          historyStore.saveConversation(copy ? {...conversationData, copy: true} : conversationData)
            .then(result => {
              Analytics.event(Analytics.EVENTS.CHAT_HISTORY_SAVE, {
                op: 'create', agent: sessionAgent, conversation_id: currentId,
                duration_ms: Date.now() - _saveStart,
              });
              // Update conversation ID to use the backend-assigned UUID for subsequent updates
              if (result.id && result.id !== currentId) {
                conversationIdRef.current = result.id;
                setConversationId(result.id);
              }
            })
            .catch(error => {
              Analytics.event(Analytics.EVENTS.CHAT_HISTORY_SAVE, {
                op: 'create', agent: sessionAgent, conversation_id: currentId,
                duration_ms: Date.now() - _saveStart, error: error.message,
              });
              console.error('Failed to CREATE conversation:', error);
              toast.error('Failed to save conversation to history: ' + error.message, {
                autoClose: 5000,
                hideProgressBar: false,
              });
            });
        } else {
          // UPDATE existing conversation in cloud (subsequent turns)
          // For searches, route to updateSearch to avoid Decimal serialization issues with
          // categoryTotals/queryEntities. The backend merges partial updates.
          // Include context.title when we just generated a new title (shouldGenerateTitle).
          const updateData = isSearchBasedConversation
            ? {
                metadata: conversationData.metadata,
                turns: conversationData.turns,
                context: { title: conversationData.context.title },
              }
            : conversationData;
          const updatePromise = isSearchBasedConversation
            ? historyStore.updateSearch(currentId, updateData)
            : historyStore.updateConversation(currentId, updateData);
          const _updateStart = Date.now();
          updatePromise
            .then(() => {
              Analytics.event(Analytics.EVENTS.CHAT_HISTORY_SAVE, {
                op: 'update', agent: sessionAgent, conversation_id: currentId,
                turn_count: conversationData.turns?.length,
                duration_ms: Date.now() - _updateStart,
              });
            })
            .catch(error => {
              Analytics.event(Analytics.EVENTS.CHAT_HISTORY_SAVE, {
                op: 'update', agent: sessionAgent, conversation_id: currentId,
                turn_count: conversationData.turns?.length,
                duration_ms: Date.now() - _updateStart, error: error.message,
              });
              console.error('Failed to UPDATE conversation:', error);
              // If 404, the conversation was deleted. Don't recreate it.
              if (error.status !== 404) {
                toast.error('Failed to update conversation history: ' + error.message, {
                  autoClose: 5000,
                  hideProgressBar: false,
                });
              }
            });
        }
      } else {
        console.debug('[saveConversationToHistory] Skipping cloud save', {
          isOnline: historyStore?.isOnline,
          isAuthenticated: userStore?.isAuthenticated
        });
      }
    } catch (e) {
      console.warn("Failed to save conversation to history", e);
    }
  }, [conversationId, convo, searchParams, editedTemplates, sessionAgent, conversationContext, conversationWithContext, helpOnly, model, userStore, generateConversationTitleViaLLM, generateFallbackTitle]);

  /**
   * Restores a conversation from saved state
   * ONLY loads the conversation - does NOT create/save.
   * Caller is responsible for CREATE if needed.
   *
   * @param {Object} conversation - The conversation to restore
   */
  const restoreConversation = useCallback((conversation) => {
    if (!conversation || !conversation.turns) {
      return false;
    }

    const {context = null, id: topLevelId} = conversation;

    // Extract conversation identity and authors
    const restoredId = topLevelId; // ID is always at top level
    const restoredAuthors = context?.authors || (context?.user ? [context.user] : []);

    // Backward compatibility: If conversation has no UUID (old format), generate one
    // Caller is responsible for CREATE
    const newId = restoredId || uuidv4();

    // CRITICAL: Set ID and context BEFORE loading turns
    // This ensures the UUID is available when the useEffect fires after convo.length changes
    // Also update the ref to keep it in sync
    conversationIdRef.current = newId;

    setConversationId(newId);
    setConversationContext({
      authors: restoredAuthors,
      title: context?.title || null
    });
    // If the conversation already has a title (manual rename or prior LLM generation),
    // suppress LLM title generation so it doesn't overwrite on the next save.
    titleGeneratedRef.current = !!context?.title;

    // Restore template customizations
    if (context?.editedTemplates) {
      Object.keys(editedTemplates).forEach(k => delete editedTemplates[k]);
      Object.entries(context.editedTemplates).forEach(([k, v]) => editedTemplates[k] = v);
    }
    if (context?.searchParams) {
      // Note: Restored searchParams are ignored in favor of current searchParams prop
      // which is always passed fresh from the parent component (SearchBox).
      // This ensures conversations reference the current search, not a stale cached one.
    }

    // Make sure we clear any text that might have been extant
    setPromptText("");
    // Clear attachments so errored/pending uploads from the previous conversation
    // don't persist into the restored conversation
    setAttachments([]);

    // Clear source references when restoring a conversation
    // The sources will be rebuilt as the conversation content renders
    sourceReferencesStore.clearSources();

    // Mark that a load is in progress so the useLayoutEffect below fires
    // onHistoryLoadEnd after turns are painted to the DOM.
    pendingLoadClearRef.current = true;
    setLoadCount(c => c + 1);

    // Load conversation turns. PLEX-6303: when the last turn may still be running (a reload
    // mid-handoff), its trailing tool call is left unrepaired until the backend says whether
    // the turn is running, so a call that hasn't failed is never shown, or sent to the model,
    // as failed. A running turn is marked, and the view says so instead of offering Resume.
    const { result } = loadStoredConversation(client, conversation, {
      conversationId: newId,
      isCurrent: () => conversationIdRef.current === newId,
    });
    // PLEX-6491 (Tim, 2026-10-09): classic chat gets v2's fork-on-write. A conversation
    // someone else owns (the backend's `owned`, which only a conversation read carries) is
    // marked after the load, which clears the mark, so its first send saves the user's own
    // copy first (LLMClient._forkFirst, and onFork below).
    runInAction(() => { client._forkSourceId = conversation.owned === false ? newId : null; });

    // PLEX-6253: shown, so read. Searches carry no unread marker, and the welcome conversation and
    // someone else's have none of this user's to clear.
    if (historyType !== 'search' && restoredId && restoredId !== WELCOME_CONVERSATION_ID
        && conversation.owned !== false) {
      rootStore.historyStore?.markRead?.(restoredId);
    }

    return result;
  }, [client, editedTemplates, sourceReferencesStore, historyType]);

  // PLEX-6491: the copy itself, as useConversationPersistence.onFork saves it for v2. It is saved under
  // the shared conversation's id with copy=true; the backend creates it under a fresh id and
  // answers with it (rd #280), and this chat moves to that id. The URL's history_id follows
  // conversationId (onDownloadContextChange, then App.js).
  useEffect(() => {
    if (!client) return undefined;
    const prev = client._onFork;
    client.onFork = async (sourceId) => {
      const data = conversationWithContext(client.turns || [], searchParams, editedTemplates, true);
      data.id = sourceId;
      data.metadata = {turnCount: data.turns?.length || 0, model: client.model || data.context?.model};
      const result = await rootStore.historyStore.saveConversation({...data, copy: true});
      const forkId = result?.id;
      if (!forkId || forkId === sourceId) return forkId;   // the engine refuses it
      conversationIdRef.current = forkId;
      setConversationId(forkId);
      return forkId;
    };
    return () => { client.onFork = prev || null; };
  }, [client, conversationWithContext, searchParams, editedTemplates]);

  // PLEX-6303: while this conversation's turn runs elsewhere, ask until it has ended, then
  // load what it stored. The draft prompt and attachments are left alone.
  const reloadEndedTurn = useCallback(async (isStale) => {
    const id = conversationIdRef.current;
    const item = await rootStore.historyStore?.getConversation?.(id, { fresh: true });
    if (isStale() || conversationIdRef.current !== id || !item?.turns?.length) return;
    loadConversation(item);
    // PLEX-6253: shown, so read (nobody was subscribed to its stream as it ended).
    rootStore.historyStore?.markRead?.(id);
  }, [loadConversation]);
  useRunningTurnWatch(client, { conversationId, reload: reloadEndedTurn });

  // Signal load complete after turns have been painted to the DOM.
  // useLayoutEffect runs synchronously after DOM mutations, before the browser paints —
  // we defer the callback to the next frame so painted content is visible first.
  useLayoutEffect(() => {
    if (!pendingLoadClearRef.current || convo.length === 0) return;
    pendingLoadClearRef.current = false;
    const raf = requestAnimationFrame(() => {
      onHistoryLoadEnd?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [convo.length, loadCount]); // loadCount ensures effect fires even when length is unchanged; onHistoryLoadEnd excluded — ref guard ensures single fire per load

  // Auto-scroll hook - must be defined before functions that use it
  const [convoScrollerRef, tailRef, autoScroll, enableAutoScroll, getScrollDiagnostics] = useAutoScroll(null, loadInitialConversation);

  // Sources drawer toggle animates .chat-session's padding-right (app.css), which resizes
  // the ancestor of floated/sticky turn bubbles. Browsers can leave those bubbles laid out
  // with stale dimensions once the transition ends, causing overlapping/cut-off text until
  // something forces a fresh reflow (PLEX-6072). Force one once the transition settles.
  useEffect(() => {
    const el = convoScrollerRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      el.style.display = "none";
      void el.offsetHeight;
      el.style.display = "";
    }, 320);
    return () => clearTimeout(timer);
  }, [sourceReferencesStore.isDrawerOpen, convoScrollerRef]);

  // Load initial conversation if configured (after restoreConversation and enableAutoScroll are defined)
  useInitialConversation({
    loadInitialConversation,
    convo,
    restoreConversation,
    enableAutoScroll,
    setInitialConversationLoaded
  });

  // Load a history item on mount when historyId is provided.
  // historyType='search' for modal/search context, 'conversation' for standalone chat.
  useEffect(() => {
    if (!historyId || convo.length > 0 || isStreamReplaying) return;
    if (userDiscardedRef.current) {
      historyRestorePendingRef.current = false; // User explicitly cleared — don't reload
      return;
    }
    if (!userStore?.isAuthenticated) return; // still pending — retry once auth resolves

    const { historyStore } = rootStore;
    if (!historyStore?.isOnline) return; // still pending — retry once back online

    const getter = historyType === 'search' ? historyStore.getSearch : historyStore.getConversation;
    getter.call(historyStore, historyId).then((item) => {
      if (item?.turns?.length) restoreConversation(item);
    }).catch((error) => {
      console.warn('[ChatSession] Failed to load history item:', error);
    }).finally(() => {
      historyRestorePendingRef.current = false;
    });
  }, [historyId, historyType, convo.length, userStore?.isAuthenticated, restoreConversation, isStreamReplaying]);

  /**
   * Discard conversation from a given turn index onwards
   */
  const discardConversation = useCallback((idx = 0) => {
    autoScroll(true);

    // Cancel any active editing
    setEditingTurnIndex(null);

    const turn = convo[idx];
    // For user prompts: discard this turn and everything after (keep 0..idx-1)
    // For assistant turns: keep this turn, discard everything after (keep 0..idx)
    // The backend can handle a dangling tool_use as the final assistant turn.
    // Nothing kept is a new conversation, anything kept a fork (see discardPlan).
    const {isUserPrompt, keepCount, newConversation} = discardPlan(convo, idx);

    // Extract text from the turn if it's a user prompt (not tool results)
    const prompt = isUserPrompt
      ? turn.content.map(el => el.text || '').join('\n')
      : '';

    if (newConversation && convo.length > 0) {
      Analytics.event(Analytics.EVENTS.CHAT_END, {
        agent: sessionAgent,
        conversation_id: conversationId,
        turn_count: convo.length,
      });
    }
    discardAfter(keepCount);

    if (newConversation) {
      // Clear the ref FIRST before any state updates that might trigger saves
      conversationIdRef.current = null;
      // Prevent the historyId effect from reloading the conversation we just cleared
      userDiscardedRef.current = true;

      // Reset all conversation-related state (including title in conversationContext)
      setConversationId(null);
      setConversationContext(null);
      setAttachments([]);
      // Clear any template customizations
      Object.keys(editedTemplates).forEach(key => delete editedTemplates[key]);
      setTemplateVersion(prev => prev + 1); // Force template UI refresh
      // Clear source references when starting new conversation
      sourceReferencesStore.clearSources();
      sourceReferencesStore.closeDrawer(); // New conversation: close drawer (no sources yet)
    } else {
      // Fork detected: discarding mid-conversation creates a new branch.
      // Assign a new UUID immediately so the save below can use it.
      const sourceId = conversationIdRef.current === WELCOME_CONVERSATION_ID ? null : conversationIdRef.current;
      const newId = uuidv4();
      pendingForkRef.current = pendingFork(sourceId, newId, keepCount);
      conversationIdRef.current = newId;
      setConversationId(newId);
      // Keep titleGeneratedRef true when forking from a titled conversation — the forked
      // branch inherits the (suffixed) title and should not regenerate it via LLM.
      titleGeneratedRef.current = !!conversationContext?.title;
      setConversationContext(prev => ({
        ...prev,
        id: null,
        title: applyTitleSuffix(prev?.title, `(truncated at turn ${idx + 1})`),
      }));
      // Save the truncated conversation as a new history entry
      setTimeout(() => saveConversationToHistory(true), 100);
    }

    // Restore prompt text to input if this was a user prompt
    if (isUserPrompt) {
      setPromptText(prompt);
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.select();
      }, 50);
    }
  }, [autoScroll, setEditingTurnIndex, convo, discardAfter, setConversationContext, conversationIdRef, setAttachments, editedTemplates, setTemplateVersion, sourceReferencesStore, setPromptText, inputRef, saveConversationToHistory, applyTitleSuffix]);

  // Notify parent about download context changes
  useEffect(() => {
    if (onDownloadContextChange) {
      onDownloadContextChange({
        conversationId, // Top-level ID (matches server structure)
        client,
        searchParams,
        editedTemplates,
        conversationContext,
        aiTemplates,
        dsConfig,
        extractConversationText,
        includeToolUse: showAdvancedFeatures,
        helpOnly,
        saveConversationToHistory,
        focusInput: () => {
          setTimeout(() => inputRef?.current?.focus(), 100);
        },
        restoreConversation: (conversation) => {
          return restoreConversation(conversation);
        },
        onNewConversation: () => {
          discardConversation(0);
          setPromptText("");
        },
        onClearConversation: () => {
          discardConversation(0);
        },
        onLoadConversation: (data) => {
          restoreConversation(data);
        },
      });
    }
  }, [onDownloadContextChange, conversationId, client, searchParams, editedTemplates, conversationContext, aiTemplates, dsConfig, showAdvancedFeatures, helpOnly, saveConversationToHistory, restoreConversation, discardConversation]);
  // Only block submission if both busy and has partials
  // If not busy, we'll auto-fix the partials on send
  const blockSubmission = busy && hasPartialMessages || hasActiveUploads;
  const convoActive = convo.some(turn => turn?.['role'] === ROLE_USER);
  const showConversation = onSubmit === null && convoActive;

  // Not memoized: convo is a MobX-observed array that LLMClient.loadConversation
  // mutates in place (this._turns.length = 0; this._turns.push(...)) rather than
  // replacing, so its object identity never changes across a conversation reload.
  // A useMemo keyed on [convo, ...] would never see a "changed" dependency and would
  // stay stuck at its stale initial value forever — which is exactly why the resume
  // button failed to appear after restoring a genuinely-interrupted conversation on
  // page reload. This recomputation is a cheap single pass (same cost class as
  // convoActive above), and the component is already wrapped in mobx-react's
  // observer(), which re-renders on any relevant observable change — so recomputing
  // directly on every render is both correct and consistent with convoActive's pattern.
  // PLEX-6303: a turn still running on the backend isn't interrupted, even though this view
  // isn't attached to it; Resume would dispatch a second turn alongside it.
  const turnRunningElsewhere = !busy && !!client.turnRunning;
  // PLEX-5967: the one definition of "interrupted", shared with the v2 chat
  // (src/llm/interruption.js). It also says where: when the stuck call opened a sub-agent
  // that was itself cut off, Resume re-runs that sub-agent from the beginning.
  const interruption = convo.length > 0 && !busy && !turnRunningElsewhere
                       ? interruptionSite(convo, client.subAgentTurns) : null;
  const isInterrupted = !!interruption;
  const resumeRestartsSubTask = interruption?.scope === "subagent";

  // Placeholder text for input - simple prompts only (no blocking state explanations)
  const userInputPlaceholder = convoActive
                               ? (busy ? "Plexy is thinking..."
                                  : turnRunningElsewhere ? "This response is still being worked on…"
                                  : isInterrupted ? "Interrupted, continue?"
                                  : "Reply to Plexy...")
                               : searchParams
                                 ? "Ask about these results, analyze patterns, or generate a report..."
                                 : helpOnly
                                   ? "Ask what plex can do or how to use it effectively"
                                   : "Use Plex to learn about Plex, ask about research, analyze data, or explore datasets...";

  // Determine if input should be disabled and why.
  // Typing is only blocked for hard blockers (search results pending, uploads, turn editing).
  // Submission is additionally blocked while streaming — let users draft while waiting.
  const inputTypingDisabled = waitingForResults || hasActiveUploads || editingTurnIndex !== null;
  const inputDisabled = inputTypingDisabled || blockSubmission;
  const inputDisabledReason = waitingForResults ? "Waiting for search results..."
                              : hasActiveUploads ? "Waiting for file uploads to complete..."
                              : blockSubmission ? "Plexy is processing — interrupt to send a new message"
                              : null;

  const canInterrupt = waitingForResults || (convo.length > 0 && busy);

  // Handle messages from chrome extension to iframe which provide page content
  const handleWindowMessage = event => {
    if (!isIFrame || event?.data?.type !== "pageContent" || convo.length > 0) {
      return;
    }
    const htmlContent = event.data.html;
    const title = event.data.title || "Page Content";
    const file = new File([htmlContent], title, {type: "text/html"});
    setAttachments(atts => {
      if (atts.filter(f => f.name === title).length === 0) {
        uploadFile(ExternalRESTAPI.Upload, file, userStore.authData)
          .then(result => setAttachments(atts => {
            if (!atts.some(f => f.name === title)) {
              return [...atts, file];
            }
            return atts;
          }));
      }
      return atts;
    })
  };

  // Prompt persistence is handled in sendChatMessage to avoid saving on every keystroke

  useEffect(() => {
    if (isIFrame) {
      window.addEventListener("message", handleWindowMessage);
      window.parent.postMessage({ "type": "autoplexReady" }, window.location.origin);
      return () => {
        window.removeEventListener('message', handleWindowMessage);
      }
    }
  }, [handleWindowMessage]);


  // Sync page title when conversation title becomes available (e.g. after backend CREATE response)
  useEffect(() => {
    if (conversationContext?.title) {
      setPageTitle(conversationContext.title);
    }
  }, [conversationContext?.title]);

  // Title generation is handled explicitly:
  // 1. When saving a conversation with context.title
  // 2. When user clicks "regenerate title" in HistoryDrawer
  // We do NOT auto-generate on convo.length changes to avoid unnecessary LLM calls


  // Helper function to build final templates
  // For regular users: append user's custom text to base templates (augmentation)
  // For admins/power users: use edited template as-is (full control)
  const buildFinalTemplates = () => {
    const finalTemplates = {};
    Object.keys(editedTemplates).forEach(key => {
      const baseTemplate = aiTemplates[key] || "";
      const userCustomText = editedTemplates[key] || "";
      if (userCustomText) {
        if (showAdvancedFeatures) {
          // Admin/power user: use edited template directly (full template editing)
          finalTemplates[key] = userCustomText;
        } else {
          // Regular user: append custom text to base template (augmentation mode)
          finalTemplates[key] = `${baseTemplate}\n\n${userCustomText}`;
        }
      }
    });
    return finalTemplates;
  };

  // Check if any templates have been customized
  const hasCustomizations = Object.keys(editedTemplates).some(key =>
    !showAdvancedFeatures && (key === "report-prompt" || key === "presentation-prompt")
  );

  const handleTemplateChange = (value) => {
    if (value === "__no-customizations__" || value === "__customize__") {
      // "No customizations" was selected - don't apply customizations, but keep them in memory
      setApplyCustomizations(false);
      setSelectedTemplate(null);
      setShowTemplateEditor(false);
    } else {
      // A specific template was selected - enable customizations
      setApplyCustomizations(true);
      setSelectedTemplate(value);
      // For admins/power users: show full base template or edited version
      // For regular users: start with blank input - user text will be APPENDED to base template
      if (showAdvancedFeatures) {
        setTemplateText(editedTemplates[value] || aiTemplates[value] || "");
      } else {
        setTemplateText(editedTemplates[value] || "");
      }
      setShowTemplateEditor(true);
    }
  };

  useEffect(() => {
    if (selectedTemplate) {
      // For admins/power users: restore edited version or show full base template
      // For regular users: restore only the user's custom text (augmentation mode)
      if (showAdvancedFeatures) {
        setTemplateText(editedTemplates[selectedTemplate] || aiTemplates[selectedTemplate] || "");
      } else {
        setTemplateText(editedTemplates[selectedTemplate] || "");
      }
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (selectedTemplate) {
      const base = showAdvancedFeatures ? (aiTemplates[selectedTemplate] || "") : "";
      if (templateText !== base) {
        // Store modified text (differs from base)
        editedTemplates[selectedTemplate] = templateText;
      } else {
        // Text matches base - not modified
        delete editedTemplates[selectedTemplate];
      }
      // Force re-render by incrementing version
      setTemplateVersion(v => v + 1);
    }
  }, [templateText]);

  useEffect(() => {
    if (autoFocus) {
      inputRef?.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!busy) {
      const id = setTimeout(() => {
        inputRef?.current?.focus();
      }, 100);
      return () => clearTimeout(id);
    }
  }, [busy])

  // Show escalating wait status when we've been waiting with no stream for >15s.
  // Clears as soon as the first streaming event arrives (isStreaming becomes true)
  // or the request completes/fails (busy becomes false).
  useEffect(() => {
    clearTimeout(waitStatusTimerRef.current);
    if (!waitingForResponse || isStreaming) {
      setWaitStatus(null);
      return;
    }
    waitStatusTimerRef.current = setTimeout(() => {
      setWaitStatus("Still connecting to Plexy — this may take a moment…");
    }, 15000);
    return () => clearTimeout(waitStatusTimerRef.current);
  }, [waitingForResponse, isStreaming])

  // Setup welcome onboarding interactions
  useWelcomeOnboarding(inputRef);

  const sendChatMessage = (text, attachments ) => {
    // Guard against sending before a pending historyId restore resolves — otherwise
    // convo.length===0 below is misread as "new conversation" and mints a fresh id,
    // silently orphaning the conversation still being fetched (PLEX-6391/WEBAPP-E1).
    if (historyRestorePendingRef.current) {
      toast.warn('Still restoring this conversation — try again in a moment.');
      return;
    }

    // Stall detector: schedule a heartbeat 500ms from now. If the main thread is blocked
    // (Chrome wait/kill scenario), the setTimeout fires late — the delta reveals the stall.
    const _submitT0 = Date.now();
    setTimeout(() => {
      const stallMs = Date.now() - _submitT0 - 500;
      if (stallMs > 200) {
        console.warn(`[ChatSession] Main thread stall detected: ${stallMs}ms after submit`, {
          textLength: text.length,
          convoLength: convo.length,
        });
        Sentry.captureMessage(`Main thread stall after submit: ${stallMs}ms`, {
          level: "warning",
          fingerprint: ["chat-submit-stall"],
          extra: { stallMs, textLength: text.length, convoLength: convo.length },
        });
      }
    }, 500);

    // Enable autoscroll when user sends a message (if it was disabled for initial load)
    enableAutoScroll();

    // Cancel any active editing
    setEditingTurnIndex(null);

    // Clear discard flag - user is starting fresh, allow future loads if needed
    userDiscardedRef.current = false;

    // Clear initial conversation flag - now user has interacted, so persistence is enabled
    if (initialConversationLoaded) {
      setInitialConversationLoaded(false);
    }

    // If there are partial messages and LLM is not busy, clean them up first
    // as if the user had interrupted the conversation
    if (hasPartialMessages && !busy) {
      interrupt();
    }

    // If this is the first turn in a new conversation, set conversation ID
    // For search-based conversations, use the historyId (URL); otherwise generate a UUID
    // Also handle the fork case: ref cleared synchronously by discardAfter but state still stale
    // Treat welcome conversation (sentinel UUID) as new — user started chatting from the
    // initial/welcome template, so we need a real UUID to save to history.
    const isWelcomeConversation = conversationIdRef.current === WELCOME_CONVERSATION_ID;
    const isNewConversation = (convo.length === 0 && !conversationId) || isWelcomeConversation;
    const isFork = !isWelcomeConversation && !conversationIdRef.current && !!conversationId;
    if (isNewConversation || isFork) {
      const isSearchBased = !isFork && historyType === 'search' && !!historyId;
      // PLEX-6545: always mint a fresh placeholder id, even for a search-based conversation.
      // It's provisional — needed synchronously so turn 1 can send before any round-trip —
      // not a claim the backend has to honor; it stays sole UUID authority and may remint
      // (as it already does on the copy-of-shared-conversation path). Reusing the search's
      // own id (historyId, derived from the canonical URL via searchUUIDFromURL) meant two
      // different users starting a chat off the same resolved search collided on id — a
      // conversation's id should never be derived/shared like a plain search's is.
      const newConversationId = uuidv4();
      conversationIdRef.current = newConversationId;
      setConversationId(newConversationId);
      titleGeneratedRef.current = false;
      setConversationContext({
        authors: [userName],
        title: text.substring(0, 60),
      });
      if (isNewConversation) {
        Analytics.event(Analytics.EVENTS.CHAT_START, { agent: sessionAgent, conversation_id: newConversationId });
      }
      console.debug('[sendChatMessage] Set conversation ID', {
        conversationId: newConversationId,
        isSearchBased,
        isFork,
        searchUrl: isSearchBased ? historyId : 'none'
      });
    }

    const userTurnIndex = Math.floor(convo.length / 2); // 0-based index of user turns
    const currentConversationId = conversationIdRef.current;
    Analytics.event(Analytics.EVENTS.CHAT_TURN_SENT, {
      agent: sessionAgent,
      turn_index: userTurnIndex,
      conversation_id: currentConversationId,
      conversation_link: currentConversationId ? `${window.location.origin}/#llm&history_id=${currentConversationId}` : undefined,
      via_polling: client.pollingMode ?? false,
    });
    sendPrompt({
                 text, attachments,
                 chatSettings,
                 templates: disablePrompts
                   ? Object.fromEntries(Object.keys(aiTemplates).map(k => [k, ""]))
                   : (applyCustomizations ? buildFinalTemplates() : {}),
                 useMarkdown,
                 searchParams,
                 useTools: !disableTools,
                 historyId: conversationIdRef.current,
                 ...takePendingFork(pendingForkRef, conversationIdRef.current),
               });
    setPromptText("");
    setAttachments(null);

    // Explicit save after user submits
    // For NEW conversations, save immediately with explicit flag
    // For existing conversations, wait for turn to be added
    if (!helpOnly) {
      if (isNewConversation) {
        // NEW conversation - save immediately with the prompt text
        // This creates the conversation in history before the turn is added
        saveConversationToHistory(true, text);
      } else {
        // EXISTING conversation - wait for turn to be added
        setTimeout(() => {
          if (conversationIdRef.current) saveConversationToHistory(false);
        }, 100);
      }
    }
  };
  sendChatMessageRef.current = sendChatMessage;

  // Debounced save: heavy parallel tool_use fan-out can fire onTurnComplete dozens
  // of times per second (once per sub-agent message_stop). Each save does a
  // synchronous toJS() clone of the full turns array plus a network round-trip;
  // without coalescing, saves stack up faster than they complete and the browser
  // freezes under the backlog (WEBAPP-lockup). leading:true keeps the first save
  // immediate; trailing collapses the rest into one save after the burst settles.
  const debouncedSaveConversationToHistory = useDebounce(saveConversationToHistory, 1000);

  // Save on every completed turn (prompt, assistant response, tool-result) and fire analytics
  // when the session ends.  LLMClient calls onTurnComplete after each message_stop and
  // after each fully-assembled tool-result message, so we always capture incremental progress
  // even during long-running multi-tool sessions where busy never goes false.
  useEffect(() => {
    if (helpOnly) return;
    client.onTurnComplete = () => {
      // PLEX-6383: while the backend persists the in-flight dispatch, it writes the
      // record at every message_stop itself, so saving the whole conversation here
      // too would only upload it again. That dispatch gets one client save, when it
      // settles (below); the backend keeps whatever it already wrote and takes from
      // that save only turns its own write lacks (R4, check-then-save-if-missing).
      if (client.deferTurnSaves) return;
      if (conversationIdRef.current) debouncedSaveConversationToHistory();
    };
    return () => {
      client.onTurnComplete = null;
    };
  }, [client, helpOnly, debouncedSaveConversationToHistory]);

  // PLEX-6383: the one client save for a dispatch the backend persisted (see
  // onTurnComplete above), when it settles. Debounced, so it coalesces with a final
  // onTurnComplete that lands after the stream has already closed.
  const prevBusyForSaveRef = useRef(busy);
  useEffect(() => {
    const wasBusy = prevBusyForSaveRef.current;
    prevBusyForSaveRef.current = busy;
    if (wasBusy && !busy && !helpOnly && client.historyPersisted && conversationIdRef.current) {
      debouncedSaveConversationToHistory();
    }
  }, [busy, helpOnly, client, debouncedSaveConversationToHistory]);

  // PLEX-6253: a turn that ended in view has been seen, so it's read, even if the backend
  // counted no subscriber (this tab mid-reconnect as the last message went out).
  const prevBusyForReadRef = useRef(busy);
  useEffect(() => {
    const wasBusy = prevBusyForReadRef.current;
    prevBusyForReadRef.current = busy;
    if (wasBusy && !busy && !helpOnly && historyType !== 'search' && conversationIdRef.current) {
      rootStore.historyStore?.markReadAfterTurn?.(conversationIdRef.current);
    }
  }, [busy, helpOnly, historyType]);

  // Analytics: fire CHAT_TURN_RECEIVED when the session completes (busy → false)
  const prevBusyRef = useRef(busy);
  useEffect(() => {
    const wasBusy = prevBusyRef.current;
    prevBusyRef.current = busy;
    if (wasBusy && !busy && convo.length > 0 && !helpOnly) {
      const lastTurn = convo[convo.length - 1];
      if (lastTurn?.role === ROLE_ASSISTANT) {
        const responseText = lastTurn.content?.map(b => b.text || "").join("") || "";
        Analytics.event(Analytics.EVENTS.CHAT_TURN_RECEIVED, {
          agent: sessionAgent,
          turn_index: Math.floor((convo.length - 1) / 2),
          conversation_id: conversationIdRef.current,
          conversation_link: conversationIdRef.current ? `${window.location.origin}/#llm&history_id=${conversationIdRef.current}` : undefined,
          via_polling: client.pollingMode ?? false,
        });
      }
    }
  }, [busy, convo.length, helpOnly, sessionAgent]);

  const handlePaste = async e => {
    if (convo.length > 0) {
      // Don't try to restore conversations when one already exists
      // User should discard the current conversation first
      return;
    }

    // Check if there's existing input text that isn't fully selected
    const target = e.target;
    const hasUnselectedText = promptText &&
      (target.selectionStart !== 0 || target.selectionEnd !== promptText.length);

    if (hasUnselectedText) {
      // Allow normal paste behavior when there's unselected text
      return;
    }

    const text = e.clipboardData?.getData('Text') || "";
    if (!text) return;

    const parsed = safeParseJSON(text);
    const looksLikeConversation = parsed !== null && (Array.isArray(parsed) || (typeof parsed === 'object' && 'turns' in parsed));

    if (!looksLikeConversation) {
      // Not JSON or no turns key — allow normal paste
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const validated = validateContext(parsed);
    Analytics.event(Analytics.EVENTS.CHAT_CONVERSATION_PASTE, {
      result: validated ? 'restored' : 'invalid',
      turn_count: validated?.turns?.length ?? (Array.isArray(parsed) ? parsed.length : null),
      has_id: !!(validated?.id),
    });
    if (!validated) {
      toast.error("Could not load conversation: invalid format");
      return;
    }

    await restoreConversationFromText(text);
  }

  // Shared logic for paste and drag/drop: resolve UUID, restore, and save to history if needed
  const restoreConversationFromText = async (text) => {
    const conversation = validateContext(safeParseJSON(text));
    if (!conversation) return;

    const { id: restoredId } = conversation;
    let conversationToRestore = conversation;
    let shouldCreate = false;

    if (!restoredId) {
      // No UUID - will be generated by restoreConversation, CREATE needed
      shouldCreate = true;
    } else {
      // Has UUID - check if it exists and matches
      const { historyStore } = rootStore;
      if (historyStore?.isOnline && userStore?.isAuthenticated) {
        try {
          const existingConversation = await historyStore.getConversation(restoredId);
          if (!existingConversation) {
            // Not found, or another user's v2 conversation (PLEX-6757: both read as missing) —
            // CREATE asking for a copy, which keeps the same UUID when it is free
            restoreCopyIdRef.current = restoredId;
            shouldCreate = true;
          } else if (existingConversation.owned === false) {
            // Another user's v1 conversation, readable by id (PLEX-6757) — the user's own copy
            // under a new UUID, as a 403 used to give
            conversationToRestore = { ...conversation, id: null };
            shouldCreate = true;
          } else {
            const existingHash = await hashConversation(existingConversation.turns);
            const restoredHash = await hashConversation(conversation.turns);

            if (existingHash === restoredHash) {
              // UUID exists and matches - just load (no CREATE)
              shouldCreate = false;
            } else {
              // UUID exists but content differs - generate new UUID and CREATE
              conversationToRestore = { ...conversation, id: null };
              shouldCreate = true;
            }
          }
        } catch (error) {
          if (error.status === 404 || error.message?.includes('not found')) {
            restoreCopyIdRef.current = restoredId;
            shouldCreate = true;
          } else if (error?.name === 'NotAuthorized') {
            // Cross-user item — pasted from another user's conversation; generate new UUID
            conversationToRestore = { ...conversation, id: null };
            shouldCreate = true;
          } else {
            // Network error - generate new UUID to be safe
            conversationToRestore = { ...conversation, id: null };
            shouldCreate = true;
          }
        }
      } else {
        // Offline - assume needs CREATE. Nothing said whether the id is free, so ask for a copy
        // (PLEX-6757): the backend keeps it when it is free and mints one when it isn't
        restoreCopyIdRef.current = restoredId;
        shouldCreate = true;
      }
    }

    if (restoreConversation(conversationToRestore) && shouldCreate && !helpOnly) {
      setTimeout(() => saveConversationToHistory(), 100);
    }
  };

  const handleDragOver = e => {
    // Allow drop by preventing default
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDropConversation = e => {
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    const firstFile = files[0];
    if (firstFile.type === 'application/json' || firstFile.name.endsWith('.json') || firstFile.name.endsWith('.plex')) {
      e.preventDefault();
      e.stopPropagation();
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        const validated = validateContext(safeParseJSON(text));
        Analytics.event(Analytics.EVENTS.CHAT_CONVERSATION_DROP, {
          source: 'outside-textarea',
          filename: firstFile.name,
          result: validated ? (convo.length > 0 ? 'blocked-existing' : 'restored') : 'invalid',
          turn_count: validated?.turns?.length ?? null,
          has_id: !!(validated?.id),
        });
        if (validated) {
          if (convo.length > 0) {
            toast.warning("Please discard the current conversation before loading a new one");
          } else {
            await restoreConversationFromText(text);
          }
        } else {
          toast.error("Could not load conversation: invalid format");
        }
      };
      reader.onerror = () => toast.error("Failed to read dropped file");
      reader.readAsText(firstFile);
    }
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) {
      return;
    }

    // Check if the first file is a .plex or JSON conversation file
    const firstFile = files[0];
    const isPlexFile = firstFile.name.endsWith('.plex');
    const isJsonFile = firstFile.type === 'application/json' || firstFile.name.endsWith('.json');
    if (isPlexFile || isJsonFile) {
      // Try to read and restore as conversation
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        const validated = validateContext(safeParseJSON(text));
        const isConversation = !!validated;
        const dropResult = isConversation
          ? (convo.length > 0 ? 'blocked-existing' : 'restored')
          : (isPlexFile ? 'invalid' : 'attached');
        Analytics.event(Analytics.EVENTS.CHAT_CONVERSATION_DROP, {
          source: 'textarea',
          filename: firstFile.name,
          result: dropResult,
          turn_count: validated?.turns?.length ?? null,
          has_id: !!(validated?.id),
        });
        if (isConversation) {
          if (convo.length > 0) {
            toast.warning("Please discard the current conversation before loading a new one");
          } else {
            await restoreConversationFromText(text);
          }
        } else if (isPlexFile) {
          // .plex files are always conversations — never attach if invalid
          toast.error("Could not load conversation: invalid format");
        } else {
          // .json that isn't a valid conversation — attach as file
          handleFilesAsAttachments(files);
        }
      };
      reader.onerror = () => {
        toast.error("Failed to read dropped file");
      };
      reader.readAsText(firstFile);
    } else {
      // Not a JSON file, add as attachment(s)
      handleFilesAsAttachments(files);
    }
  };

  const handleFilesAsAttachments = (files) => {
    if (!allowAttachments || !useTools) {
      toast.warning("File attachments are not enabled for this conversation");
      return;
    }

    // Add files to attachments, enforcing per-message limits. Only append the
    // raw File objects here — the <FileAttachments> component (rendered below)
    // owns uploading: it starts an upload itself as soon as a value it's given
    // lacks an uploadId. Uploading here too would fire a second, duplicate
    // upload of the same file and swallow its own errors silently.
    setAttachments(prev => {
      const existingFiles = prev || [];
      const newFiles = files.filter(newFile =>
        !existingFiles.some(existing =>
          existing.name === newFile.name && existing.size === newFile.size
        )
      );

      if (newFiles.length === 0) {
        toast.info("File(s) already attached");
        return prev;
      }

      // Enforce document and image limits
      const existingDocs = existingFiles.filter(f => !isImage(f)).length;
      const existingImages = existingFiles.filter(f => isImage(f)).length;
      const accepted = [];
      let docCount = existingDocs;
      let imageCount = existingImages;

      for (const file of newFiles) {
        if (isImage(file)) {
          if (imageCount >= IMAGE_LIMIT) {
            toast.warn(`You may attach no more than ${IMAGE_LIMIT} images`);
            break;
          }
          imageCount++;
        } else {
          if (docCount >= DOC_LIMIT) {
            toast.warn(`You may attach no more than ${DOC_LIMIT} non-image documents`);
            break;
          }
          docCount++;
        }
        accepted.push(file);
      }

      if (accepted.length === 0) return prev;

      return [...existingFiles, ...accepted];
    });
  };

  const validateContext = conversation => {
    // Accommodate legacy conversations with array of turns ONLY
    const patched = Array.isArray(conversation)
                    ? {context: {}, turns: conversation}
                    : conversation;
    if (!patched || typeof patched !== "object") return null;
    if (!Array.isArray(patched.turns) || patched.turns.length === 0) return null;
    // Reject turns missing role — a hand-edited or corrupted import would otherwise
    // get stored and produce a loadConversation error on every subsequent page load.
    const VALID_ROLES = new Set(['user', 'assistant', 'system']);
    const invalidTurn = patched.turns.find(t => !t || !VALID_ROLES.has(t.role));
    if (invalidTurn) {
      console.warn('validateContext: rejected conversation with invalid turn role', invalidTurn);
      return null;
    }
    return patched;
  };

  const handleSendClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const text = (promptText || "").trim();
    if (text) {
      sendChatMessage(text, attachments);
    }
  }

  const handleLoadConversationFromHistory = useCallback((conversation) => {
    // Confirm if there's an active conversation
    if (convo.length > 0) {
      setConfirmDialog({
        open: true,
        message: "Loading a new conversation will discard the current one. Continue?",
        onConfirm: () => {
          const validated = validateContext(conversation);
          if (validated) {
            restoreConversation(validated);
            toast.success("Conversation loaded");
          } else {
            toast.error("Failed to load conversation");
          }
        },
      });
      return;
    }

    // Validate and restore the conversation
    const validated = validateContext(conversation);
    if (validated) {
      restoreConversation(validated);
      toast.success("Conversation loaded");
    } else {
      toast.error("Failed to load conversation");
    }
  }, [convo, validateContext, restoreConversation]);

  const handleNewConversationFromHistory = useCallback(() => {
    discardConversation(0);
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 100);
  }, []);

  const handleEditPrompt = useCallback(() => {
    // If there's a conversation, extract the first user prompt
    if (convo.length > 0) {
      const firstUserTurn = convo.find(turn =>
        turn.role === ROLE_USER && !turn.content?.some(block => block.toolResult)
      );

      if (firstUserTurn) {
        const promptToEdit = firstUserTurn.content
          .map(block => block.text || '')
          .join('\n');
        setPromptText(promptToEdit);
        inputRef?.current?.focus();
        // Move cursor to end of text
        setTimeout(() => {
          const input = inputRef.current;
          if (input && input.setSelectionRange) {
            const length = promptToEdit.length;
            input.setSelectionRange(length, length);
          }
        }, 0);
      }
    }
  }, [convo]);

  const handleNewConversation = useCallback(() => {
    if (convo.length > 0 || promptText.trim()) {
      setConfirmDialog({
        open: true,
        message: "Starting a new conversation will discard the current conversation and prompt. Continue?",
        onConfirm: () => {
          setEditingTurnIndex(null);
          discardConversation(0);
          setPromptText("");
          inputRef?.current?.focus();
        },
      });
      return;
    }

    // Cancel any active editing
    setEditingTurnIndex(null);

    discardConversation(0);
    setPromptText("");
    inputRef?.current?.focus();
  }, [convo, promptText]);

  // Start editing a prompt in-place
  const handleEditPromptAtTurn = useCallback((turnIndex) => {
    setEditingTurnIndex(turnIndex);
  }, []);

  // Save edited prompt - discard from edited turn and submit as new
  const handleSaveEdit = useCallback((newPromptText, newAttachments = null) => {
    if (editingTurnIndex === null) return;

    // Exit editing mode
    setEditingTurnIndex(null);

    // Discard conversation from the turn being edited.
    // discardConversation() restores the original prompt to the input box for idx>0,
    // so we immediately clear it to prevent a flash of the old text before sendChatMessage.
    discardConversation(editingTurnIndex);
    setPromptText("");

    // Submit the edited prompt as a new message
    // Use setTimeout to ensure the discard completes first. Call via sendChatMessageRef,
    // not the closed-over sendChatMessage — this callback's closure was captured before
    // discardConversation's re-render, so a direct reference would still see pre-discard
    // conversationId/convo state (see sendChatMessageRef's comment above).
    setTimeout(() => {
      sendChatMessageRef.current(newPromptText, newAttachments);
    }, 0);
  }, [editingTurnIndex, discardConversation, setPromptText]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingTurnIndex(null);
  }, []);

  // Admin-only: replay conversation with simulated streaming to test checkpoint rendering.
  // Varies chunk sizes to exercise different paragraph-boundary scenarios.
  const handleStreamReplayTest = useCallback(async () => {
    if (convo.length === 0 || isStreamReplaying) return;

    // Snapshot completed turns (deep copy)
    const snapshot = toJS(convo);
    const abortController = new AbortController();
    streamReplayAbortRef.current = abortController;
    setIsStreamReplaying(true);

    // Chunk size patterns: small (char-by-char near links), medium, large
    const chunkSizes = [3, 8, 15, 40, 80, 5, 12, 25, 60, 2];
    let chunkIdx = 0;

    try {
      // Use discardAfter(0) to clear — matches existing pattern and resets metrics
      client.discardAfter(0);
      sourceReferencesStore.clearSources();
      // Allow React to render the empty state before replaying
      await new Promise(r => setTimeout(r, 500));

      for (const turn of snapshot) {
        if (abortController.signal.aborted) break;

        if (turn.role === ROLE_ASSISTANT && turn.content?.some(b => b.text)) {
          // Simulate streaming for assistant text blocks
          const simulatedTurn = { ...turn, partial: true, content: turn.content.map(b => {
            if (b.text) return { ...b, text: '', partial: true };
            return { ...b };
          })};

          runInAction(() => { client.turns.push(simulatedTurn); });

          for (let blockIdx = 0; blockIdx < turn.content.length; blockIdx++) {
            const block = turn.content[blockIdx];
            if (!block.text) continue;

            const fullText = block.text;
            let pos = 0;

            while (pos < fullText.length && !abortController.signal.aborted) {
              const size = chunkSizes[chunkIdx % chunkSizes.length];
              chunkIdx++;
              const end = Math.min(pos + size, fullText.length);
              const chunk = fullText.slice(pos, end);
              pos = end;

              runInAction(() => {
                const liveBlock = client.turns[client.turns.length - 1].content[blockIdx];
                liveBlock.text = (liveBlock.text || '') + chunk;
              });

              await new Promise(r => setTimeout(r, 30 + Math.random() * 40));
            }

            // Mark block complete
            runInAction(() => {
              const liveBlock = client.turns[client.turns.length - 1].content[blockIdx];
              delete liveBlock.partial;
            });
          }

          // Mark turn complete
          runInAction(() => {
            const liveTurn = client.turns[client.turns.length - 1];
            delete liveTurn.partial;
          });
        } else {
          // Non-assistant turns: add instantly
          runInAction(() => { client.turns.push({ ...turn }); });
        }

        await new Promise(r => setTimeout(r, 200));
      }
    } finally {
      setIsStreamReplaying(false);
      streamReplayAbortRef.current = null;
    }
  }, [convo, isStreamReplaying, client, discardAfter, sourceReferencesStore]);

  const replayConversation = (idx = 0)=> {
    autoScroll(true);

    // Cancel any active editing
    setEditingTurnIndex(null);

    // Clear sources — they'll be regenerated as the new response streams in
    sourceReferencesStore.clearSources();

    // Any replay (full or partial) creates a new branch — assign a fresh UUID immediately
    // so onTurnComplete callbacks during streaming have a valid ID to save against.
    // (Setting ref to null then relying on sendChatMessage to reassign caused a race where
    // LLMClient fired message_stop before sendChatMessage ran — Sentry WEBAPP-M4.)
    const sourceId = conversationIdRef.current === WELCOME_CONVERSATION_ID ? null : conversationIdRef.current;
    const forkId = uuidv4();
    conversationIdRef.current = forkId;
    setConversationId(forkId);
    setConversationContext(prev => ({
      ...prev,
      id: forkId,
      title: applyTitleSuffix(prev?.title, `(regenerated from turn ${idx + 1})`),
    }));

    replayFrom(idx, {
      chatSettings,
      templates: disablePrompts
        ? Object.fromEntries(Object.keys(aiTemplates).map(k => [k, ""]))
        : buildFinalTemplates(),
      useMarkdown,
      searchParams,
      useTools: !disableTools,
      // PLEX-6383: dispatch under the fork's id. With no id, the engine falls back to
      // its own, the SOURCE conversation's, and once the backend writes history it
      // would append the regenerated turns to the source. replayFrom keeps the first
      // idx turns and resends turn idx, or keeps idx + 1 and continues from a
      // tool-result turn: the fork keeps exactly what replayFrom keeps.
      historyId: forkId,
      ...forkLineage(sourceId, forkId, replayKeptTurns(idx)),
    });
  };

  // Resume from an interrupted conversation — continue from the last turn without forking.
  // Unlike replayConversation, this preserves the existing conversationId so history saves
  // to the same item rather than creating a branch.
  // PLEX-6187: the backend re-drives the crashed turn from its stored record (resumeFrom),
  // which falls back to today's continue where it can't. busy stays false while the
  // running-turn check is out, so a second click then would send a second request.
  const resumingRef = useRef(false);
  const handleResume = useCallback(async () => {
    if (!isInterrupted || busy || convo.length === 0 || resumingRef.current) return;
    resumingRef.current = true;
    try {
      // PLEX-6303: the turn may still be running on the backend (another tab, or a reload whose
      // check hasn't come back). Then nothing is dispatched, and the view says it is running.
      if (await markIfTurnRunning(client, conversationIdRef.current)) return;
      autoScroll(true);
      setEditingTurnIndex(null);
      sourceReferencesStore.clearSources();
      await resumeFrom(convo.length - 1, {
        chatSettings,
        templates: disablePrompts
          ? Object.fromEntries(Object.keys(aiTemplates).map(k => [k, ""]))
          : buildFinalTemplates(),
        useMarkdown,
        searchParams,
        useTools: !disableTools,
        historyId: conversationIdRef.current,
      });
    } finally {
      resumingRef.current = false;
    }
  }, [client, isInterrupted, busy, convo, chatSettings, disablePrompts, aiTemplates, buildFinalTemplates,
      useMarkdown, searchParams, disableTools, resumeFrom, sourceReferencesStore]);

  const inputChanged = (e) => {
    const text = e.target.value || "";
    setPromptText(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !(e.shiftKey || e.altKey)) {
      e.stopPropagation();
      e.preventDefault();
      const text = promptText.trim();
      if (text && !waitingForResults && !blockSubmission) {
        if (onSubmit) {
          onSubmit(text);
        }
        else {
          sendChatMessage(text, attachments);
        }
      }
    }
  };


  const handleMolEditorAdd = (term) => {
    Analytics.event(Analytics.EVENTS.KETCHER_ADD_TO_CHAT, { smiles: (term || "").slice(0, 200) });
    const textarea = inputRef.current;
    const pos = textarea?.selectionStart ?? promptText.length;
    const before = promptText.slice(0, pos);
    const after = promptText.slice(pos);
    const needsLeadingSpace = before.length > 0 && !before.endsWith(" ") && !before.endsWith("\n");
    const needsTrailingSpace = after.length > 0 && !after.startsWith(" ") && !after.startsWith("\n");
    const insertion = `${needsLeadingSpace ? " " : ""}${term}${needsTrailingSpace ? " " : ""}`;
    const newText = before + insertion + after;
    setPromptText(newText);
    const newPos = before.length + insertion.length;
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(newPos, newPos);
    }, 50);
  };

  // TODO: serialize whatever's currently available and move ID lookups
  // to when a given assistant turn is completed, rewriting that text

  const getTextForClipboard = useCallback((asJSON = false) => {
    if (asJSON && !helpOnly) {
      return JSON.stringify(conversationWithContext(convo, searchParams, editedTemplates));
    }
    return conversationAsText(convo, showAdvancedFeatures);
  }, [conversationWithContext, searchParams, conversationAsText, showAdvancedFeatures]);

  const downloadSummary = (conversation, outputFormat = "pptx", verbose = false) => {
    if (outputFormat === "pptx") setIsDownloadingPPTX(true);
    else if (outputFormat === "docx") setIsDownloadingDOCX(true);
    const options = {
      input_format: "markdown",
      output_format: outputFormat,
      mime_type: outputFormat === "pptx"
                 ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                 : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      verbose,
    }
    // Get conversation as JSON (not text) so backend can parse and prune it
    const conversationJSON = JSON.stringify(conversationWithContext(convo, searchParams, editedTemplates));
    const key = outputFormat === "pptx" ? "presentation-prompt" : "report-prompt";
    // Build prompt by appending user's custom text to base template
    const baseTemplate = aiTemplates[key] || `Make a report about the following text\n${TEXT_PLACEHOLDER}`;
    const userCustomText = editedTemplates[key] || "";
    const prompt = userCustomText ? `${baseTemplate}\n\n${userCustomText}` : baseTemplate;
    const fullPrompt = prompt.replace(TEXT_PLACEHOLDER, `<input-text>${conversationJSON}</input-text>`);
    return oneshot(fullPrompt, auth, options)
      .then(response => {
        // Parse Content-Disposition header to extract filename.
        // Fall back to a name derived from the conversation title if the header
        // is absent or unparseable.
        const header = response.headers.get('content-disposition');
        const titleSlug = (conversationContext?.title || '')
          .replace(/[/\\?%*:|"<>]/g, '')   // strip filesystem-illegal chars
          .replace(/\s+/g, ' ')            // collapse whitespace
          .trim()
          .substring(0, 80);
        let filename = titleSlug ? `${titleSlug}.${outputFormat}` : `summary.${outputFormat}`;

        if (header) {
          try {
            const disposition = parseContentDisposition(header);
            filename = disposition.parameters.filename || filename;
          } catch (e) {
            console.warn('Failed to parse Content-Disposition header:', header, e);

            // Log malformed headers to Sentry for investigation
            captureMessage('Malformed Content-Disposition header in summary generation', {
              level: "warning",
              fingerprint: ["content-disposition-parse-error"],
              tags: {
                error_type: "content_disposition_parse",
                output_format: outputFormat,
              },
              context: {
                content_disposition: {
                  header: header,
                  error_message: e?.message || String(e),
                  output_format: outputFormat,
                },
              },
            });
          }
        }

        return response.blob()
          .then(blob => {
            saveBlobContent(blob, filename);
          });
      })
      .catch(e => {
        console.error("Summary generation failed", e);
        toast.error("Summary generation failed");

        // Capture summary generation errors to Sentry with conversation and prompt
        const conversationData = conversationWithContext(convo, searchParams, editedTemplates);
        captureMessage(`Summary generation failed: ${e?.message || String(e)}`, {
          level: "error",
          asException: true,
          errorName: "SummaryGenerationError",
          fingerprint: ["summary-generation-error", outputFormat],
          tags: {
            error_type: "summary_generation",
            output_format: outputFormat,
          },
          context: {
            summary_error: {
              error_message: e?.message || String(e),
              output_format: outputFormat,
              verbose: verbose,
              conversation_turn_count: convo.length,
            },
          },
          attachments: [
            {
              filename: "conversation.json",
              data: JSON.stringify(conversationData, null, 2),
              contentType: "application/json",
            },
            {
              filename: "prompt.txt",
              data: fullPrompt,
              contentType: "text/plain",
            },
          ],
        });
      })
      .finally(() => {
        if (outputFormat === "pptx") {
          setIsDownloadingPPTX(false);
        }
        else if (outputFormat === "docx") {
          setIsDownloadingDOCX(false);
        }
      });
  };

  const allowModelSelection = showAdvancedFeatures || /^.*?@(aws|amazon\.co(m|\.uk))$/.test(userName);

  // Calculate cost estimate based on token usage and selected model
  const getEstimatedCost = () => {
    return calculateCost({
      inputTokens,
      outputTokens,
      cacheReadInputTokens,
      cacheWriteInputTokens
    }, model || aiModelDefault);
  };


  const submitIssue = (json) => {
    setBugReportDialog({ open: true, message: "", json });
  };

  const handleBugReportSubmit = () => {
    const { message: msg, json } = bugReportDialog;
    setBugReportDialog({ open: false, message: "", json: null });
    if (!msg.trim()) return;
    Analytics.event(Analytics.EVENTS.CHAT_BUG_REPORT, { agent: sessionAgent, conversation_id: conversationId });

    const url = SearchContext.getAppSearchURL(searchParams);
    const {
      Sentry: sentryOptions,
    } = getConfig();
    if (!sentryOptions.DSN) {
      toast.warning("Sentry not configured");
      return;
    }

    const timestamp = new Date().toISOString();
    const scrollDiagnostics = getScrollDiagnostics ? getScrollDiagnostics() : [];

    const attachments = json ? [{
      filename: "conversation.json",
      data: typeof(json) === "string" ? json : JSON.stringify(json, null, 2),
      contentType: "application/json",
    }] : [];
    if (scrollDiagnostics.length > 0) {
      attachments.push({
        filename: "scroll-diagnostics.json",
        data: JSON.stringify(scrollDiagnostics, null, 2),
        contentType: "application/json",
      });
    }

    const eventId = captureMessage(msg, {
      asException: true,
      errorName: "User Bug Report",
      captureStackTrace: false,
      fingerprint: ["user-bug-report", userName, timestamp],
      tags: {
        report_type: "user_bug_report",
      },
      context: {
        bug_report: {
          user_description: msg,
          search_url: url,
          timestamp,
        },
      },
      attachments,
    });
    if (eventId !== null) {
      toast.info("The current conversation has been submitted for error review");
    } else {
      console.error("[Bug Report] Failed to submit bug report");
      toast.error("Failed to submit bug report to Sentry");
    }
  };

  return (
    <ConversationProvider
      turns={convo}
      searchParams={searchParams}
      editedTemplates={editedTemplates}
      sourceReferencesStore={sourceReferencesStore}
      isStreamReplaying={isStreamReplaying}
    >
      <div key="chat" className={`chat-session${className ? " " + className : ""}`} onDrop={handleDropConversation} onDragOver={e => e.preventDefault()}>
        {showConversation && (
          <div key="scroller" ref={convoScrollerRef} className={`conversation-scroller conversation-scroller-primary${showOverlay ? " loading-conversation" : ""}`} >
            <SafeRenderer>
              <Conversation
                key={`conversation-${conversationId || "new"}`}
                className={hasFlavorAgent ? "guide-conversation" : ""}
                turns={convo}
                client={client}
                welcome={showWelcome && (<div key="welcome" className={ROLE_SYSTEM}>{WELCOME}</div>)}
                showAdvancedFeatures={showAdvancedFeatures}
                showTimestamps={sessionAgent === AGENT_GUIDE}
                showDetails={isPowerUser || isAdmin}
                agentId={agentId}
                busy={busy}
                resolveSourceLinks={resolveSourceLinks}
                replayConversation={replayConversation}
                discardConversation={discardConversation}
                userStore={userStore}
                editingTurnIndex={editingTurnIndex}
                onEditPrompt={handleEditPromptAtTurn}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                subAgentTurns={client.subAgentTurns}
              />
            </SafeRenderer>
            <div key="convo-tail" ref={tailRef} className={"convo-tail"} />
          </div>)}
      {convoActive && (
        <div key="controls" className={"controls"}>
          <button
            key="controls-submit-bug"
            className={"glyphicon glyphicon-bug"}
            title={"Report an issue"}
            onClick={e => submitIssue(getTextForClipboard(true))}
          />
          <CopyToClipboardButton
            key="controls-copy"
            className={"glyphicon glyphicon-copy"}
            title={!helpOnly ? "Click: copy as text\nAlt+click: copy as JSON" : "Copy conversation"}
            getText={(e) => { Analytics.event(Analytics.EVENTS.CHAT_COPY, { conversation_id: conversationId }); return getTextForClipboard(false); }}
            getAltText={!helpOnly ? (e) => { Analytics.event(Analytics.EVENTS.CHAT_COPY, { conversation_id: conversationId, format: "json" }); return getTextForClipboard(true); } : null}
            getAltExtraFlavors={!helpOnly ? () => ({
              "web text/plex-conversation": getTextForClipboard(true),
              "application/json": getTextForClipboard(true),
            }) : null}
            getExtraFlavors={!helpOnly ? () => ({
              "web text/plex-conversation": getTextForClipboard(true),
              "application/json": getTextForClipboard(true),
            }) : null}
            getFallbackAnnotation={() => "Copied as plain text — multi-flavor clipboard requires Chrome or Edge"}
          />
          {isAdmin && convoActive && (
            <button
              key="controls-stream-replay"
              className={`glyphicon glyphicon-play-circle${isStreamReplaying ? " active" : ""}`}
              title={isStreamReplaying ? "Stop streaming replay test" : "Replay conversation with simulated streaming (tests link checkpoint rendering)"}
              onClick={() => {
                if (isStreamReplaying && streamReplayAbortRef.current) {
                  streamReplayAbortRef.current.abort();
                } else {
                  handleStreamReplayTest();
                }
              }}
              disabled={busy && !isStreamReplaying}
            />
          )}
          {!helpOnly && (
          <button
            key="controls-sources"
            className={"sources-toggle-btn"}
            title={"Toggle sources sidebar"}
            onClick={() => { Analytics.event(Analytics.EVENTS.CHAT_SOURCES_TOGGLE, { open: !sourceReferencesStore.drawerOpen }); sourceReferencesStore.toggleDrawer(); }}
          >
            🔗 Sources
            {sourceReferencesStore.sources.length > 0 && (
              <span className="source-count">
                {' '}({sourceReferencesStore.sources.length})
              </span>
            )}
          </button>
          )}
          <LoadingAnimation key="loading-animation" style={{visibility: busy ? "visible" : "hidden"}}/>
        </div>
      )}
      {waitStatus && (
        <div key="wait-status" className="chat-wait-status" role="status" aria-live="polite">
          {waitStatus}
        </div>
      )}
      {!waitStatus && turnRunningElsewhere && (
        <div key="running-turn" className="chat-wait-status" role="status" aria-live="polite"
             data-testid="chat-running-turn">
          This response is still being worked on. Long analyses can take several minutes; it will
          appear here when it finishes.
        </div>
      )}
      {!waitStatus && useTools && resumeRestartsSubTask && (
        <div key="resume-restarts" className="chat-wait-status" role="status" aria-live="polite"
             data-testid="chat-resume-restarts">
          The previous response was interrupted while a {interruption.toolName ? `${interruption.toolName} ` : "sub-"}task
          was still running. Resuming restarts that task from the beginning.
        </div>
      )}
      <SharedCopyNotice client={client} />
      <div key="chat-control" className={`chat-box-control${isDragOver ? ' drag-over' : ''}`}>
        <div key="input-wrapper" className="prompt-input-wrapper">
          <SplitButton
            id="chat-attach-split"
            className="attach-inside-split"
            title={<span className="glyphicon glyphicon-paperclip"></span>}
            disabled={inputDisabled}
            onClick={() => composerFileAttachmentsRef.current?.click()}
          >
            {useTools && allowAttachments && (
              <Dropdown.Item
                onClick={() => composerFileAttachmentsRef.current?.click()}
              >
                <span className="glyphicon glyphicon-paperclip"></span> Attach files
              </Dropdown.Item>
            )}
            <Dropdown.Item
              onClick={() => {
                Analytics.event(Analytics.EVENTS.KETCHER_OPEN, { source: "chat" });
                setShowMolEditor(true);
              }}
            >
              <img src={molEditorImage} alt="" className="mol-editor-menu-icon" /> Draw structure
            </Dropdown.Item>
          </SplitButton>
          <Textarea
            key={"input"}
            className={"prompt"}
            ref={inputRef}
            disabled={inputTypingDisabled}
            placeholder={userInputPlaceholder}
            value={promptText || ""}
            onChange={inputChanged}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        </div>
        <button
          key={"send"}
          className={"send"}
          disabled={!promptText.trim() || inputDisabled}
          title={inputDisabledReason || (promptText ? "Send this message to Plexy" : "Type something to send to Plexy")}
          onClick={handleSendClick}
        >
          <span className={"glyphicon glyphicon-arrow-up"}></span>
        </button>
        {useTools && isInterrupted && !busy && (
          <button
            key={"resume"}
            className={"resume"}
            title={resumeRestartsSubTask
                   ? "Re-run the interrupted sub-task and continue the response"
                   : "Resume interrupted conversation"}
            onClick={handleResume}
          >
            <span className={"glyphicon glyphicon-play"}></span>
          </button>
        )}
        {useTools && (
          <button
            key={"stop"}
            className={`stop${canInterrupt ? "" : " disabled"}`}
            disabled={!canInterrupt}
            title={canInterrupt ? "Interrupt Plexy" : ""}
            onClick={() => { if (canInterrupt) { Analytics.event(Analytics.EVENTS.CHAT_INTERRUPT, { agent: sessionAgent, conversation_id: conversationId }); interrupt(); } }}
          >
            <span className={`glyphicon glyphicon-stop${canInterrupt ? "" : " disabled"}`}></span>
          </button>
        )}
      </div>
      {useTools && allowAttachments && (
        <div className={"chat-actions"}>
          <FileAttachments
            ref={composerFileAttachmentsRef}
            value={attachments}
            onChange={files => setAttachments(files?.length && files || null)}
            onFileUploadError={(file) => toast.error(`Failed to upload ${file.name}`)}
            authData={userStore}
            documentLimit={DOC_LIMIT}
            documentSizeLimit={DOC_SIZE_LIMIT}
            imageLimit={IMAGE_LIMIT}
            imageSizeLimit={IMAGE_SIZE_LIMIT}
            accept={FILE_INPUT_ACCEPT.join(',')}
            uploadFile={uploadFile}
            hideButton={true}
          />
          {allowModelSelection && !showSettings && (
            <SimpleSelect
              className={"power-user model-select"}
              options={Object.keys(aiModels)
                .sort((a, b) => {
                  const aMatch = /^(US|Global)/.test(aiModels[a]);
                  const bMatch = /^(US|Global)/.test(aiModels[b]);
                  if (aMatch && !bMatch) return -1;
                  if (bMatch && !aMatch) return 1;
                  return aiModels[a].toLowerCase().localeCompare(aiModels[b].toLowerCase());
                })}
              value={model || aiModelDefault}
              getOptionLabel={x => `${aiModels[x]}${extractVersion(x)}${appendModelId(x)}`}
              onChange={value => client.model = value}
            />
          )}
          {showAdmin && (
            <div key={"metrics"}
              className={`llm-metrics admin`}
            >
              <span title={"input tokens"}>{inputTokens.toLocaleString()}</span>/<span title={"output tokens"}>{outputTokens.toLocaleString()}</span>
              {" "}
              <span>
                [<span title={"cache read tokens"}>{cacheReadInputTokens.toLocaleString()}</span>/
                <span title={"cache write tokens"}>{cacheWriteInputTokens.toLocaleString()}</span>/
                <span title={"total tokens"}>{totalTokens.toLocaleString()}</span>]
                {" "}
                <span title={"estimated cost"}>${getEstimatedCost().toFixed(2)}</span>
              </span>
              {" "}
              <span title={`elapsed time`}>{formatTimestamp(latencyMs)}</span>
            </div>
          )}
        </div>
      )}
      {(showAdvancedFeatures || convoActive) && !helpOnly && (
        <div key="templates" className={showAdvancedFeatures ? "power-user ai-templates" : "ai-templates"}>
          <div className={"template-controls"}>
            <SimpleSelect
              key={`${selectedTemplate || "__no-customizations__"}-${templateVersion}`}
              className={showAdvancedFeatures ? "power-user template-select" : "template-select"}
              options={showAdvancedFeatures
                ? (selectedTemplate ? Object.keys(aiTemplates).sort() : ["__no-customizations__", ...Object.keys(aiTemplates).sort()])
                : (selectedTemplate ? ["__no-customizations__", "report-prompt", "presentation-prompt"] : ["report-prompt", "presentation-prompt"])}
              value={selectedTemplate || "__no-customizations__"}
              onChange={handleTemplateChange}
              getOptionLabel={(key) => {
                if (key === "__no-customizations__") {
                  return selectedTemplate ? "No customizations" : "Customize...";
                }
                const isModified = editedTemplates[key] !== undefined;
                if (!showAdvancedFeatures) {
                  if (key === "report-prompt") return `Report Generation${isModified ? " *" : ""}`;
                  if (key === "presentation-prompt") return `Presentation Generation${isModified ? " *" : ""}`;
                }
                return `${key}${isModified ? " *" : ""}`;
              }}
            />
            {isAdmin && (
              <>
                <Checkbox
                  className={"admin"}
                  label={"Disable prompts"}
                  title={"Disable use of prompts/templates in LLM requests"}
                  checked={disablePrompts}
                  onChange={setDisablePrompts}
                />
                <Checkbox
                  className={"admin"}
                  label={"Disable tools"}
                  title={"Disable use of tools in LLM requests"}
                  checked={disableTools}
                  onChange={setDisableTools}
                />
              </>
            )}
          </div>
          {showTemplateEditor && (
            <textarea
              className={showAdvancedFeatures ? "power-user template-editor" : "template-editor"}
              placeholder={selectedTemplate === "extra-prompt"
                ? "Place any additional LLM instructions here"
                : "How would you like to customize the output?"}
              onKeyDown={e => {
                if (e.key === "Escape") {
                  setTemplateText(showAdvancedFeatures ? (aiTemplates[selectedTemplate] || "") : "");
                }
              }}
              onChange={e => setTemplateText(e.target.value)}
              value={templateText}
            />
          )}
        </div>
      )}
      {showAdvancedFeatures && showSettings && searchParams && (
        <ChatSettings
          key={"settings"}
          onChange={setChatSettings}
          categories={categories}
          disabled={convoActive}
          userStore={userStore}
          appStatusStore={appStatusStore}
        />
      )}
      {!helpOnly && <SourceReferencesDrawer store={sourceReferencesStore} containerRef={modalContentRef} />}

      <Modal show={confirmDialog.open} onHide={() => setConfirmDialog({ open: false, message: "", onConfirm: null })} centered className="plex-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="glyphicon glyphicon-question-sign" style={{ marginRight: '8px' }}></span>
            Confirm
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p>{confirmDialog.message}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDialog({ open: false, message: "", onConfirm: null })}>Cancel</Button>
          <Button variant="primary" onClick={() => { const fn = confirmDialog.onConfirm; setConfirmDialog({ open: false, message: "", onConfirm: null }); fn?.(); }}>Continue</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={bugReportDialog.open} onHide={() => setBugReportDialog({ open: false, message: "", json: null })} centered className="plex-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="glyphicon glyphicon-bug" style={{ marginRight: '8px' }}></span>
            Report an Issue
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormControl
            as="textarea"
            rows={3}
            placeholder="Briefly tell us what happened"
            value={bugReportDialog.message}
            onChange={e => setBugReportDialog(d => ({ ...d, message: e.target.value }))}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBugReportDialog({ open: false, message: "", json: null })}>Cancel</Button>
          <Button variant="primary" disabled={!bugReportDialog.message.trim()} onClick={handleBugReportSubmit}>Submit</Button>
        </Modal.Footer>
      </Modal>

      {sourceReferencesStore.accessInstructionsModal && (
        <Modal show={true} onHide={() => sourceReferencesStore.hideAccessInstructions()} centered className="plex-modal">
          <Modal.Header closeButton>
            <Modal.Title>
              {sourceReferencesStore.accessInstructionsModal.datasetName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{sourceReferencesStore.accessInstructionsModal.instructions}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => sourceReferencesStore.hideAccessInstructions()}>Close</Button>
            <Button variant="primary" href={sourceReferencesStore.accessInstructionsModal.href} target="_blank" rel="noopener noreferrer" onClick={() => sourceReferencesStore.hideAccessInstructions()}>Open Source</Button>
          </Modal.Footer>
        </Modal>
      )}

      <MolecularEditorModal
        showEditor={showMolEditor}
        onCloseEditor={() => setShowMolEditor(false)}
        encoding={""}
        onAddTerm={handleMolEditorAdd}
        chatMode={true}
      />

      </div>
      {showMemoryWindow && <MemoryWindow userStore={userStore} />}
      {showVizCanvas && (
        <>
          <VisualizationCanvas
            sourceReferencesStore={sourceReferencesStore}
            onRegenerate={(id) => {
              console.info("[viz] regenerate requested for", id);
            }}
          />
          <button
            type="button"
            aria-label="Back to chat"
            className="mobile-canvas-back-btn"
            onClick={() => visualizationStore.setCanvasDismissed(true)}
          >
            ‹ Chat
          </button>
        </>
      )}
    </ConversationProvider>
  )
}));

export default ChatSession;

